<template>
  <NotFound v-if="!product?.product" />
  <ProductView v-else />
</template>

<script setup lang="ts">
import useProductStore from '~/stores/useProductStore';
import useEnvProps from '~/stores/useEnvProps';
import usePageData from '~/stores/usePageData';
import useReviews from '~/modules/reviews/composables/useReviews';
import setCorrectPath from '~/utils/setCorrectPath';
import { useUpcPreselect } from '~/modules/upc/composables/useUpcPreselect';
import { useProductProvide } from '~/modules/product/composables/useProductProvide';
import { useQuantityProvide } from '~/modules/product/composables/useQuantity';
import { useDeviceTypeProvide } from '~/composables/utils/useDeviceType';

import { onMounted, watchEffect } from 'vue';
import { loadTagLibrary } from '@vue-util/tagManager';
import useQuickShopProvider from '@nuxt-common/product-quickview/app/composables/useQuickShopProvider';
import { PROS_QUICK_VIEW_TAGGING } from '@nuxt-common/pros-collection/app/constants/features/quickShop';
import { useLoyaltyProvide } from '~/composables/loyalty/useLoyalty';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import { useProductType } from '~/composables/useProductType';
import { usePpcProvide } from '~/modules/ppc/composables/usePPC';
import { useUserProvide } from '~/composables/user/useUser';
import { useUpcValidatorProvide } from '~/modules/upc/composables/useUpcValidator';
import { useImagesProvide } from '~/composables/images/useImages';
import { useFulfilmentDataProvide } from '~/modules/availability/composables/useFulfilmentData';
import { useBigTicketATBValidatorProvide } from '~/modules/availability/composables/useBigTicketATBValidator';
import { useAvailabilityBigTicketProvide } from '~/modules/availability/composables/useAvailabilityBigTicket';
import { useBigTicketDeliveryMethodsProvide } from '~/modules/availability/composables/useBigTicketDeliveryMethods';
import { useBopsDataProvide } from '~/composables/useBopsData';
import { useMarxentProvide } from '~/modules/marxent/composables/useMarxent';
import { useProductPriceProvide } from '~/modules/price/composables/useProductPriceProvide';
import { useMainImageInteractedProvide } from '~/composables/useMainImageInteracted';
import { useProductQuickviewProvide } from '~/composables/product-quickview/useProductQuickviewProvide';
import { useConsolidatedMemberProvide } from '~/composables/useConsolidatedMember';
import useConsolidatedMemberStore from '~/stores/useConsolidatedMemberStore';
import { useWnmProvide } from '~/modules/worry-no-more/composables/useWnm';

import useBopsUpcOverride from '~/modules/availability/composables/useBopsUpcOverride';
import { useTraitsErrorActiveProvide } from '~/modules/sticky-product-traits/composables/context-providers/useTraitsErrorActive';
import useSelectedMemberId from '~/modules/latchkey/composables/useSelectedMemberId';
import fireViewTag from '~/features/analytics/viewManager';
import useHeadTags from '~/composables/useHeadTags';
import { useProductQuickviewStore } from '~/stores/useProductQuickviewStore';

const pdpProductQuickviewStore = useProductQuickviewStore();

useQuickShopProvider({
  // handler for PROS product quickview click
  onOpen: (payload) => {
    const { productId, colorId, slideoutTitle = 'Quick View' } = payload;

    if (!productId) {
      console.warn('Cannot open Quick View: productId is missing.');
      return;
    }

    // set PROS quickview tagging
    pdpProductQuickviewStore.setPdpPqvTagging(PROS_QUICK_VIEW_TAGGING);

    pdpProductQuickviewStore.setPdpPqvPayload({
      productId: String(productId),
      swatchColor: colorId,
      slideoutTitle,
    });
  },
});

const envProps = useEnvProps();
const productStore = useProductStore();
const route = useRoute();

// TODO separate out pageData and product logic so they independent, probably can deprecate pageData
const pageData = usePageData().pageData;
const product = usePageData().product;

if (import.meta.server && productStore.product) {
  const redirect = setCorrectPath(useRequestURL().pathname, productStore.product, pageData.killswitches);
  if (redirect) {
    navigateTo(
      {
        path: redirect,
        query: route.query,
      },
      { redirectCode: 301, replace: true },
    );
  }

  useHeadTags();
}

const productId = product?.product.id;
const experimentIds = pageData.experimentIds;
const killswitches = pageData.killswitches;

useDeviceTypeProvide(); // TODO this is in app.vue in old

if (product) {
  useUserProvide();
  useLoyaltyProvide();
  useProductProvide(productId, product?.product);

  const { isShopTheSetExpEnabled } = useFeatureEligibility();
  if (isShopTheSetExpEnabled) {
    useConsolidatedMemberProvide();
  }
  useProductPriceProvide({ id: productId, product: product?.product });
  useQuantityProvide();
  useUpcValidatorProvide();
  useMarxentProvide();
  useImagesProvide();
  useFulfilmentDataProvide();
  useBopsDataProvide();
  useBopsUpcOverride();
  useAvailabilityBigTicketProvide();
  useBigTicketDeliveryMethodsProvide();
  useBigTicketATBValidatorProvide();

  useUpcPreselect();
  usePpcProvide();
  useMainImageInteractedProvide();
  useProductQuickviewProvide();
  useWnmProvide();
  useTraitsErrorActiveProvide();
}

onMounted(async () => {
  const { brand, tagMngrEnv, tagMngrEnabled } = envProps.envProps;
  if (tagMngrEnabled) {
    const isAutoTaggingEnabled = killswitches?.isAutoTaggingEnabled && experimentIds?.includes('1220-23');
    if (!isAutoTaggingEnabled) {
      await loadTagLibrary(brand, tagMngrEnv, true); // Manually firing view tag and setting Tealium not to fire
    }

    const ExperimentationTagsModule = await import('@nuxt-common/util/app/utils/experimentation/ExperimentationTags');
    const ExperimentationTags = ExperimentationTagsModule.default;
    await ExperimentationTags.fireAdobeTags();
  }
});

onMounted(() => {
  if (product?.product) fireViewTag();
});

if (productStore.product) {
  const { isConsolidatedThirdVariantFlag } = useProductFlags();
  const { isShopTheSetExpEnabled } = useFeatureEligibility();

  const selectedProdId = useSelectedMemberId();

  if (import.meta.server) {
    const activeReviewProductId = isConsolidatedThirdVariantFlag || isShopTheSetExpEnabled ? selectedProdId.value : productStore.product.id;
    const reviewsStore = useReviews();
    reviewsStore.initReviewsContext({
      product: productStore.product,
      activeReviewProductId,
      members: productStore.getAllMembers,
    });
    reviewsStore.initReviews();
  }
  const { isConsolidatedPage } = useProductType();
  if (isConsolidatedThirdVariantFlag || isShopTheSetExpEnabled || isConsolidatedPage) {
    const reviewsStore = useReviews();
    const consolidatedStore = useConsolidatedMemberStore();
    // todo - remove watcher once "useSelectedMemberId" is converted into pinia store
    watchEffect(() => {
      const memberId = selectedProdId.value;
      if (isConsolidatedThirdVariantFlag || isShopTheSetExpEnabled) reviewsStore.setActiveReviewProductId(memberId);
      if (!isShopTheSetExpEnabled && memberId) consolidatedStore.selectedMember = +memberId;
    });
  }
}
</script>
